import { FC, memo, useMemo, Dispatch, SetStateAction } from 'react'

import {
  Text,
  IconTag,
  IconColumns,
  IconCalendar,
  IconEmail,
  IconPlus,
  IconCalendarDays,
  Badge,
  SequenceMenu,
  SequenceMenuItemType,
  Button,
  GreyGrey60,
  MagicTableCell,
  MagicTableCellEmpty,
  MagicTableCellScheduleStatus,
  MagicTableFilterConfig,
  MagicTable,
  useMagicTableInfiniteQuery,
  useLinkMagicTableWithSearchParams,
  MagicTableEmptyState,
  MagicTableFilterEmptyState,
  ActiveFilter,
  Flex
} from '@sequencehq/core-components'
import {
  allBillingScheduleStatuses,
  toBillingScheduleStatusBadgeProps
} from '@sequencehq/utils'
import {
  dateTimeWithFormat,
  formatISODate,
  formatScheduleEndDate,
  nth
} from '@sequencehq/utils'
import { BillingSchedulesSegmentedFilters } from 'modules/BillingSchedules/components/BillingSchedulesSegmentedFilters'
import {
  BillingSchedulesFilters,
  BillingSchedulesView
} from 'modules/BillingSchedules/types'
import { CurrentUserId } from 'components/CurrentUserId/CurrentUserId'
import Page from 'components/Page'
import { StickyPanelFrame } from 'components/Page/StickyPanelFrame'
import { ExtractAttributeTypeFromUnion } from 'lib/types/extractAttributeTypeFromUnion'
import {
  type DashboardApi20240730,
  dashboard20240730Client
} from '@sequencehq/api/dashboard/v20240730'
import { CustomerPreviewCardPill } from 'Customer/components/CustomerPreviewCard'
import {
  useBillingScheduleCustomFieldFilters,
  useBillingScheduleCustomFieldObjectPropertyColumns
} from 'modules/BillingSchedules/hooks/useBillingScheduleCustomFieldColumns'
import { BillingSchedulesExportToCsv } from 'modules/BillingSchedules/components/BillingSchedulesExportToCsv'
import { useNavigate } from 'react-router-dom'
import { add, sub } from '@sequencehq/utils/dates'
import { ArchiveScheduleMenuItem } from 'common/menuItems/schedules/ArchiveScheduleMenuItem'
import { DuplicateScheduleMenuItem } from 'common/menuItems/schedules/DuplicateScheduleMenuItem'
import { ProtectedButton, usePermissions } from 'lib/rbac'
import { ContractUploadButton } from 'Automations/index'
import { TableLinksLogos } from 'modules/Integrations'
import { useLogEvent } from 'lib/postHog/useLogEvent'
import { customerIdFilterConfig } from 'Customer/index'
import { SavedViewEditor } from 'SavedViews/components/SavedViewEditor'
import { SavedViewTitle } from 'SavedViews/components/SavedViewTitle'
import { useSavedView } from 'SavedViews/hooks/useSavedView'
import { useForcedActiveFilters } from 'SavedViews/hooks/useForcedActiveFilters'

type BillingSchedule = DashboardApi20240730.GetBillingSchedules.BillingSchedule
type BillingScheduleStatus =
  DashboardApi20240730.BillingSchedules.BillingScheduleStatus

const statusFilterOptions = () => {
  return (
    allBillingScheduleStatuses
      /**
       * PENDING is stored as ACTIVE in the database, so we need to do some backend work
       * to make it possible to filter by PENDING
       */
      .filter(status => status !== 'PENDING')
      .map(status => ({
        label: toBillingScheduleStatusBadgeProps({ status }).children,
        value: status
      }))
  )
}

const createDateFilterOptions = (type: 'before' | 'after') => {
  const today = new Date()
  const baseOptions = [
    {
      label: 'Today',
      value: formatISODate(today)
    }
  ]

  const monthOptions = [1, 3, 6].map(months => {
    if (type === 'before') {
      return {
        label: `${months} month${months > 1 ? 's' : ''} from now`,
        value: formatISODate(add(today, { months }))
      }
    } else {
      return {
        label: `${months} month${months > 1 ? 's' : ''} ago`,
        value: formatISODate(sub(today, { months }))
      }
    }
  })

  return [...baseOptions, ...monthOptions]
}

const startDateBeforeFilterOptions = createDateFilterOptions('before')
const startDateAfterFilterOptions = createDateFilterOptions('after')
const endDateBeforeFilterOptions = createDateFilterOptions('before')
const endDateAfterFilterOptions = createDateFilterOptions('after')

const recurrenceDayOfMonthFilterOptions = Array.from(
  { length: 31 },
  (_, index) => ({
    label: `${nth(`${index + 1}`)} of the month`,
    value: `${index + 1}`
  })
)

const BillingSchedules: FC<{
  title?: string
  forcedActiveFilters?: ActiveFilter[]
  kebabMenuItems?: (
    setSavingFilters: Dispatch<SetStateAction<false | ActiveFilter[]>>
  ) => SequenceMenuItemType[] | undefined
  onUpdateSavedFilters?: (
    updatedActiveFilters: ActiveFilter[],
    sortBy: string | null
  ) => void
  showSegmentedFilters?: boolean
}> = memo(
  ({
    title = 'Schedules',
    forcedActiveFilters = [],
    kebabMenuItems,
    onUpdateSavedFilters,
    showSegmentedFilters = true
  }) => {
    const navigate = useNavigate()
    const { logEvent } = useLogEvent()

    const {
      isSavedView,
      savingFilters,
      setSavingFilters,
      forceNewSavedView,
      onSaveFilters,
      onDismissSaveFilters,
      showSavedViewEditor
    } = useSavedView()

    const { allCustomFieldKeys, customFieldFilters } =
      useBillingScheduleCustomFieldFilters()

    const filters: MagicTableFilterConfig<BillingSchedulesFilters>[] = [
      {
        ...customerIdFilterConfig,
        paramName: 'customerId'
      },
      {
        type: 'multiSelect',
        paramName: 'billingScheduleStatus',
        options: statusFilterOptions(),
        format: status => (
          <Badge
            {...toBillingScheduleStatusBadgeProps({
              status: status as BillingScheduleStatus
            })}
          />
        ),
        label: 'Status',
        optionsSortFn: () => 0,
        icon: IconColumns
      },
      {
        type: 'date',
        paramName: 'starting',
        paramNameBefore: 'startingBefore',
        paramNameAfter: 'startingAfter',
        optionsBefore: startDateBeforeFilterOptions,
        optionsAfter: startDateAfterFilterOptions,
        format: (_, label) => label,
        label: 'Start date',
        stringifyDate: date => formatISODate(date),
        icon: IconCalendar
      },
      {
        type: 'date',
        paramName: 'ending',
        paramNameBefore: 'endingBefore',
        paramNameAfter: 'endingAfter',
        optionsBefore: endDateBeforeFilterOptions,
        optionsAfter: endDateAfterFilterOptions,
        format: (_, label) => label,
        label: 'End date',
        stringifyDate: date => formatISODate(date),
        icon: IconCalendar
      },
      {
        type: 'multiSelect' as const,
        paramName: 'autoSendInvoices' as const,
        options: [
          { label: 'Enabled', value: 'true' },
          { label: 'Disabled', value: 'false' }
        ],
        label: 'Auto-send invoices',
        icon: IconEmail,
        format: (value: string) => (
          <Text>{value === 'true' ? 'Yes' : 'No'}</Text>
        ),
        optionsSortFn: (a: { label: string }, b: { label: string }) => {
          if (a.label === 'Enabled') return -1
          if (b.label === 'Enabled') return 1
          return 0
        }
      },
      {
        type: 'multiSelect',
        paramName: 'recurrenceDayOfMonth',
        options: recurrenceDayOfMonthFilterOptions,
        format: (value: string) => <Text>{nth(value)}</Text>,
        label: 'Billing day',
        optionsSortFn: () => 0,
        icon: IconCalendarDays
      },
      {
        paramName: 'includeArchivedSchedules',
        type: 'toggle',
        label: 'Show archived schedules'
      },
      {
        type: 'text',
        paramName: 'label',
        label: 'Label',
        icon: IconTag
      },
      ...customFieldFilters
    ]

    const { can } = usePermissions()

    const {
      activeFilters: searchActiveFilters,
      onChangeActiveFilters: onChangeSearchActiveFilters,
      sortBy,
      onChangeSortBy
    } = useLinkMagicTableWithSearchParams(filters)

    const { activeFilters, onChangeActiveFilters } = useForcedActiveFilters({
      forcedActiveFilters,
      searchActiveFilters,
      onChangeSearchActiveFilters
    })

    // Surface the billing day column automatically while its filter is active,
    // so the value being filtered on is visible in the table (mirrors the
    // custom field columns behaviour).
    const requiredColumnIds = useMemo(
      () =>
        activeFilters.some(
          filter =>
            filter.paramName === 'recurrenceDayOfMonth' &&
            filter.type === 'multiSelect' &&
            filter.values.length > 0
        )
          ? ['recurrenceDayOfMonth']
          : [],
      [activeFilters]
    )

    const { infiniteQuery } = useMagicTableInfiniteQuery(
      dashboard20240730Client.getBillingSchedules,
      filters,
      activeFilters,
      sortBy
    )

    const customFieldObjectPropertyColumns =
      useBillingScheduleCustomFieldObjectPropertyColumns({
        allCustomFieldKeys,
        activeFilters
      })

    const richTitle = showSavedViewEditor ? (
      <SavedViewEditor
        activeFilters={savingFilters || []}
        sortBy={sortBy}
        onDismiss={onDismissSaveFilters}
        forceNewSavedView={forceNewSavedView}
        entityType="BILLING_SCHEDULES"
      />
    ) : (
      <SavedViewTitle
        title={title}
        kebabMenuItems={kebabMenuItems?.(setSavingFilters)}
      />
    )

    const additionalToolbarComponentsV3 = showSegmentedFilters
      ? {
          additionalToolbarComponentsV3: {
            start: <BillingSchedulesSegmentedFilters />
          }
        }
      : undefined

    const AddNewScheduleButton = () => {
      return (
        <ProtectedButton permission="org.billing:create">
          <Button
            variant="primary"
            leadingIcon={<IconPlus color="inherit" />}
            onClick={() => {
              logEvent({
                eventName: 'manual_new_schedule'
              })
              navigate('/billing-schedules/new')
            }}
            data-testid="TopNavigation.add-new"
          >
            New schedule
          </Button>
        </ProtectedButton>
      )
    }

    return (
      <StickyPanelFrame data-testid="billingSchedules.magicTable">
        <Page
          title={title}
          richTitle={richTitle}
          addNewConfig={
            savingFilters
              ? undefined
              : {
                  button: <AddNewScheduleButton />
                }
          }
          paddingBottom={0}
          menuButtons={
            savingFilters ? null : (
              <Flex gap="8px">
                <BillingSchedulesExportToCsv />
                <ContractUploadButton />
              </Flex>
            )
          }
        >
          <CurrentUserId>
            {userId => (
              <MagicTable<BillingSchedulesView>
                activeFilters={activeFilters}
                onChangeActiveFilters={onChangeActiveFilters}
                sortBy={sortBy}
                onChangeSortBy={onChangeSortBy}
                entityNamePlural="schedules"
                sequenceUserId={userId}
                infiniteQuery={infiniteQuery}
                objectPropertyColumns={customFieldObjectPropertyColumns}
                rowPath={row => {
                  if (!can('org.billing:update')) {
                    return `/billing-schedules/${row.id}`
                  }

                  return row.status === 'DRAFT'
                    ? `/billing-schedules/${row.id}/edit`
                    : `/billing-schedules/${row.id}`
                }}
                columns={[
                  {
                    id: 'customer',
                    accessorFn: row => ({
                      customerId: row.customerId
                    }),
                    header: 'Customer',
                    cell: value => {
                      const { customerId } = value.getValue<{
                        customerId: BillingSchedule['customerId']
                      }>()

                      return (
                        <Flex
                          alignItems="center"
                          justifyContent="space-between"
                          height="40px"
                          pl="6px"
                        >
                          <CustomerPreviewCardPill
                            customerId={customerId}
                            variant="ghost"
                            data-testid={`schedules.${customerId}.previewPill`}
                          />
                        </Flex>
                      )
                    }
                  },
                  {
                    id: 'status',
                    header: 'Status',
                    accessorFn: (row: BillingSchedule) => ({
                      status: row.status,
                      archivedAt: row.archivedAt
                    }),
                    cell: value => {
                      const { status, archivedAt } = value.getValue<{
                        status: BillingSchedule['status']
                        archivedAt: BillingSchedule['archivedAt']
                      }>()

                      return (
                        <MagicTableCellScheduleStatus
                          status={status}
                          isArchived={!!archivedAt}
                        />
                      )
                    }
                  },
                  {
                    id: 'products',
                    header: 'Products',
                    accessorFn: (model: BillingSchedule) => ({
                      planId: undefined,
                      prices: model.prices,
                      archivedAt: model.archivedAt
                    }),
                    cell: value => {
                      const { prices, archivedAt } = value.getValue<{
                        prices: ExtractAttributeTypeFromUnion<
                          BillingSchedule,
                          'prices'
                        >
                        archivedAt: ExtractAttributeTypeFromUnion<
                          BillingSchedule,
                          'archivedAt'
                        >
                      }>()

                      if (prices) {
                        return (
                          <MagicTableCell
                            textColor={archivedAt && GreyGrey60}
                            text={prices.map(p => p.name).join(', ')}
                          />
                        )
                      }

                      return null
                    }
                  },
                  {
                    id: 'startDate',
                    accessorFn: (row: BillingSchedule) => ({
                      archivedAt: row.archivedAt,
                      startDate: row.startDate
                    }),
                    header: 'Start date',
                    cell: value => {
                      const { startDate, archivedAt } = value.getValue<{
                        startDate: BillingSchedule['startDate']
                        archivedAt: BillingSchedule['archivedAt']
                      }>()

                      return (
                        <MagicTableCell
                          textColor={archivedAt && GreyGrey60}
                          text={dateTimeWithFormat(startDate, 'd MMM yyyy')}
                        />
                      )
                    },
                    enableSorting: true,
                    sortingConfiguration: {
                      availableDirections: ['DESC'],
                      sortProperty: 'START_DATE',
                      appendDirectionToSortKey: false
                    }
                  },
                  {
                    id: 'endDate',
                    accessorFn: (row: BillingSchedule) => ({
                      archivedAt: row.archivedAt,
                      endDate: row.endDate
                    }),
                    header: 'End date',
                    cell: value => {
                      const { endDate, archivedAt } = value.getValue<{
                        endDate: BillingSchedule['endDate']
                        archivedAt: BillingSchedule['archivedAt']
                      }>()

                      return (
                        <MagicTableCell
                          textColor={archivedAt && GreyGrey60}
                          text={formatScheduleEndDate(endDate)}
                        />
                      )
                    }
                  },
                  {
                    id: 'integrationIds',
                    header: 'Links',
                    accessorKey: 'integrationIds',
                    cell: value => {
                      const integrationIds =
                        value.getValue<BillingSchedule['integrationIds']>()

                      if (!integrationIds?.length) {
                        return null
                      }

                      return (
                        <MagicTableCellEmpty>
                          <TableLinksLogos
                            services={integrationIds.map(
                              integration => integration.service
                            )}
                          />
                        </MagicTableCellEmpty>
                      )
                    }
                  },
                  {
                    id: 'autoIssueInvoices',
                    accessorFn: (row: BillingSchedule) => ({
                      archivedAt: row.archivedAt,
                      autoIssueInvoices: row.autoIssueInvoices
                    }),
                    header: 'Auto-send invoices',
                    hiddenByDefault: true,
                    cell: (value: { getValue: () => unknown }) => {
                      const { autoIssueInvoices, archivedAt } =
                        value.getValue() as {
                          autoIssueInvoices: BillingSchedule['autoIssueInvoices']
                          archivedAt: BillingSchedule['archivedAt']
                        }

                      return (
                        <MagicTableCell
                          textColor={archivedAt && GreyGrey60}
                          text={autoIssueInvoices ? 'Yes' : 'No'}
                        />
                      )
                    }
                  },
                  {
                    id: 'label',
                    accessorFn: (row: BillingSchedule) => ({
                      archivedAt: row.archivedAt,
                      label: row.label
                    }),
                    header: 'Label',
                    hiddenByDefault: true,
                    cell: (value: { getValue: () => unknown }) => {
                      const { label, archivedAt } = value.getValue() as {
                        label: BillingSchedule['label']
                        archivedAt: BillingSchedule['archivedAt']
                      }

                      return (
                        <MagicTableCell
                          textColor={archivedAt && GreyGrey60}
                          text={label || ''}
                        />
                      )
                    }
                  },
                  {
                    id: 'recurrenceDayOfMonth',
                    accessorFn: (row: BillingSchedule) => ({
                      archivedAt: row.archivedAt,
                      recurrenceDayOfMonth: row.recurrenceDayOfMonth
                    }),
                    header: 'Billing day',
                    hiddenByDefault: true,
                    cell: (value: { getValue: () => unknown }) => {
                      const { recurrenceDayOfMonth, archivedAt } =
                        value.getValue() as {
                          recurrenceDayOfMonth: BillingSchedule['recurrenceDayOfMonth']
                          archivedAt: BillingSchedule['archivedAt']
                        }

                      return (
                        <MagicTableCell
                          textColor={archivedAt && GreyGrey60}
                          text={
                            recurrenceDayOfMonth
                              ? nth(`${recurrenceDayOfMonth}`)
                              : ''
                          }
                        />
                      )
                    }
                  },
                  {
                    id: 'currency',
                    accessorFn: (row: BillingSchedule) => ({
                      archivedAt: row.archivedAt,
                      currencies: [...new Set(row.prices.map(p => p.currency))]
                    }),
                    header: 'Currency',
                    hiddenByDefault: true,
                    cell: (value: { getValue: () => unknown }) => {
                      const { currencies, archivedAt } = value.getValue() as {
                        currencies: Array<
                          BillingSchedule['prices'][number]['currency']
                        >
                        archivedAt: BillingSchedule['archivedAt']
                      }

                      return (
                        <MagicTableCell
                          textColor={archivedAt && GreyGrey60}
                          text={currencies.join(', ')}
                        />
                      )
                    }
                  }
                ]}
                filters={filters}
                requiredColumnIds={requiredColumnIds}
                emptyStateUnfiltered={entityNamePlural => (
                  <MagicTableEmptyState
                    data-testid="billingSchedules.emptyState"
                    entityNamePlural={entityNamePlural}
                    imageSrc="/img/empty/schedules.png"
                    learnMoreText="Create a new billing schedule for a customer and put invoicing on autopilot"
                    learnMoreHref="https://docs.sequencehq.com/billing/create-billing-schedules"
                    buttonNew={<AddNewScheduleButton />}
                  />
                )}
                emptyStateFiltered={(entityNamePlural, clearFilters) => (
                  <MagicTableFilterEmptyState
                    entityNamePlural={entityNamePlural}
                    entityIcon={IconCalendarDays}
                    actionClearFilters={clearFilters}
                    actionViewAll={() => navigate('/billing-schedules')}
                    variant={isSavedView ? 'SAVED_VIEW' : 'TABLE'}
                  />
                )}
                onSaveFilters={
                  onSaveFilters ? () => onSaveFilters(activeFilters) : undefined
                }
                onUpdateSavedFilters={
                  onUpdateSavedFilters && isSavedView
                    ? () => onUpdateSavedFilters(activeFilters, sortBy)
                    : undefined
                }
                lockedControls={savingFilters && savingFilters.length > 0}
                kebabMenu={(model: BillingSchedule, props) => (
                  <SequenceMenu
                    data-testid={`schedules.${model.id}.menu`}
                    buttonSize="small"
                    alignment="bottom-right"
                    items={[
                      <SequenceMenu.Item
                        uuid="view-schedule"
                        key="view-schedule"
                        label="View schedule"
                        data-testid={`schedules.${model.id}.menuItem.view`}
                        onClick={() => {
                          if (!can('org.billing:update')) {
                            return navigate(`/billing-schedules/${model.id}`)
                          }

                          navigate(
                            model.status === 'DRAFT'
                              ? `/billing-schedules/${model.id}/edit`
                              : `/billing-schedules/${model.id}`
                          )
                        }}
                      />,
                      <DuplicateScheduleMenuItem
                        key="duplicate-schedule"
                        scheduleId={model.id}
                        onSuccess={(newScheduleId: string) =>
                          navigate(`/billing-schedules/${newScheduleId}/edit`)
                        }
                      />,
                      ({ setIsOpen }) =>
                        !model.archivedAt && (
                          <ArchiveScheduleMenuItem
                            key="archive-schedule"
                            data-testid={`schedules.${model.id}.menuItem.archive`}
                            scheduleId={model.id}
                            lockedByQuoteId={model.lockedByQuoteId}
                            onSuccess={() => {
                              setIsOpen?.(false)
                            }}
                          />
                        )
                    ]}
                    {...props}
                  />
                )}
                {...additionalToolbarComponentsV3}
              />
            )}
          </CurrentUserId>
        </Page>
      </StickyPanelFrame>
    )
  }
)

export default BillingSchedules
